import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  ExternalLink,
  FileSearch,
  Info,
  Linkedin,
  Lock,
  Mail,
  MailCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  GtmCompanyProfile,
  GtmDemoRunResponse,
  GtmEmailDraft,
  GtmFunnelEstimate,
  GtmLeadPreview,
  GtmSocialPlatform,
} from '@laplace/contracts'
import { Badge } from '@/components/ui/badge'
import { siteIconUrl } from '@/features/gtm/components/gtm-company-logo'
import { Button } from '@/components/ui/button'
import { trackClickGtmStartCampaign } from '@/shared/analytics'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { cn } from '@/shared/lib/utils'

const panelClassName = 'rounded-2xl border border-sidebar-border bg-card/85 p-6'

type GtmDemoResultsProps = {
  run: GtmDemoRunResponse
  onTryAnother: () => void
}

export function GtmDemoResults({ run, onTryAnother }: GtmDemoResultsProps) {
  const { t } = useTranslation('gtm')

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-500">
      {run.sampleData ? (
        <div className="flex items-start gap-2 rounded-xl border border-sidebar-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {t('sampleData')}
        </div>
      ) : null}
      {run.profile ? <GtmProfilePanel profile={run.profile} /> : null}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <GtmLeadsPanel leads={run.leads} total={run.totalLeadsFound} />
        <GtmEmailsPanel emails={run.emails} leads={run.leads} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {run.estimate ? <GtmEstimatePanel estimate={run.estimate} /> : null}
        <GtmStartPanel
          domain={run.domain}
          runId={run.id}
          onTryAnother={onTryAnother}
        />
      </div>
    </div>
  )
}

export function ChipList({
  label,
  items,
}: {
  label: string
  items: Array<string>
}) {
  if (items.length === 0) return null
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <Badge
            key={item}
            variant="outline"
            className="rounded-full border-sidebar-border bg-secondary/40 px-3 py-1 text-sm font-normal whitespace-normal text-foreground"
          >
            {item}
          </Badge>
        ))}
      </div>
    </div>
  )
}

export const SOCIAL_LABELS: Record<GtmSocialPlatform, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  github: 'GitHub',
  crunchbase: 'Crunchbase',
  producthunt: 'Product Hunt',
  youtube: 'YouTube',
  g2: 'G2',
  facebook: 'Facebook',
  instagram: 'Instagram',
}

export function pagePath(url: string): string {
  try {
    const { pathname } = new URL(url)
    return pathname === '/' ? '/' : pathname.replace(/\/$/, '')
  } catch {
    return url
  }
}

/** Research result: shown as soon as it lands, before leads are ready. */
export function GtmProfilePanel({ profile }: { profile: GtmCompanyProfile }) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const number = new Intl.NumberFormat(lang)
  const sizes = profile.icp.companySizes.map((size) => t('employees', { size }))
  const { research } = profile
  const coverage = [
    t('researchCoverage', {
      count: research.pagesRead.length,
      domain: profile.domain,
    }),
    research.sitemapPageCount
      ? t('researchSitemap', {
          pages: number.format(research.sitemapPageCount),
        })
      : null,
    research.hasLlmsTxt ? t('researchLlmsTxt') : null,
  ].filter(Boolean)

  return (
    <section className={panelClassName}>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold text-foreground">
          {t('profileTitle', { company: profile.companyName })}
        </h2>
        {profile.category ? (
          <Badge
            variant="outline"
            className="rounded-full border-upgrade-cta/40 bg-upgrade-cta/10 px-3 py-0.5 text-xs font-normal text-foreground"
          >
            {profile.category}
          </Badge>
        ) : null}
      </div>
      <p className="mt-2 text-muted-foreground">{profile.summary}</p>

      {research.pagesRead.length > 0 ? (
        <div className="mt-4 space-y-2">
          <p className="flex items-center gap-2 text-sm text-foreground">
            <FileSearch
              className="size-4 shrink-0 text-upgrade-cta"
              aria-hidden="true"
            />
            {coverage.join(' · ')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {research.pagesRead.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-sidebar-border bg-card/80 px-2 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {pagePath(url)}
              </a>
            ))}
          </div>
        </div>
      ) : null}

      {profile.people.length > 0 ? (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground">
            {t('peopleTitle')}
          </h3>
          <ul className="mt-2 space-y-1.5">
            {profile.people.map((person) => (
              <li key={person.name} className="text-sm text-foreground">
                {person.name}
                <span className="text-muted-foreground">
                  {' '}
                  · {person.role} ·{' '}
                </span>
                <a
                  href={person.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-upgrade-cta hover:underline"
                >
                  {t('personSource')}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {profile.socialProfiles.length > 0 ? (
        <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
            {t('socialProfiles')}
          </span>
          {profile.socialProfiles.map((social) => (
            <a
              key={social.url}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-foreground hover:text-upgrade-cta"
            >
              {SOCIAL_LABELS[social.platform]}
              <ExternalLink className="size-3" aria-hidden="true" />
            </a>
          ))}
        </div>
      ) : null}

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <ChipList label={t('keyFeatures')} items={profile.keyFeatures} />
        <ChipList label={t('namedCustomers')} items={profile.namedCustomers} />
      </div>

      <h3 className="mt-6 text-sm font-semibold text-foreground">
        {t('icpTitle')}
      </h3>
      <div className="mt-3 grid gap-5 md:grid-cols-2">
        <ChipList label={t('icpJobTitles')} items={profile.icp.jobTitles} />
        <ChipList label={t('icpIndustries')} items={profile.icp.industries} />
        <ChipList label={t('icpCompanySizes')} items={sizes} />
        <ChipList label={t('icpLocations')} items={profile.icp.locations} />
      </div>
      <div className="mt-5">
        <ChipList label={t('icpPainPoints')} items={profile.icp.painPoints} />
      </div>
    </section>
  )
}

export function GtmLeadsPanel({
  leads,
  total,
  wide = false,
}: {
  leads: Array<GtmLeadPreview>
  total: number
  wide?: boolean
}) {
  const { t } = useTranslation('gtm')

  return (
    <section className={panelClassName}>
      <h2 className="text-xl font-semibold text-foreground">
        {t('leadsTitle', { count: total })}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('leadsHint')}</p>
      <ul
        className={cn(
          'mt-5',
          wide
            ? 'grid gap-x-6 2xl:grid-cols-2'
            : 'divide-y divide-sidebar-border',
        )}
      >
        {leads.map((lead) => (
          <li
            key={lead.id}
            className={cn(
              'flex items-start justify-between gap-4 py-3',
              wide && 'border-b border-sidebar-border',
              lead.locked && 'opacity-70',
            )}
          >
            <div className="min-w-0">
              <div className="font-medium text-foreground">{lead.fullName}</div>
              <div className="flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
                <span>{lead.title}</span>
                <span aria-hidden="true">·</span>
                {lead.companyDomain ? (
                  <img
                    src={siteIconUrl(lead.companyDomain)}
                    alt=""
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="size-3.5 rounded-sm bg-white"
                  />
                ) : null}
                <span>{lead.companyName}</span>
                {lead.location ? <span>· {lead.location}</span> : null}
              </div>
              {lead.campaign ||
              lead.linkedinUrl ||
              lead.contact?.status === 'valid' ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {lead.campaign ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-sidebar-border px-2 py-0 text-[11px] font-normal text-muted-foreground"
                    >
                      {lead.campaign}
                    </Badge>
                  ) : null}
                  {lead.linkedinUrl ? (
                    <a
                      href={lead.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1 rounded-full border border-sidebar-border px-2 text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      <Linkedin className="size-3" aria-hidden="true" />
                      {t('leadHasLinkedin')}
                    </a>
                  ) : null}
                  {lead.contact?.status === 'valid' ? (
                    <Badge
                      variant="outline"
                      className="gap-1 rounded-full border-upgrade-cta/40 px-2 py-0 text-[11px] font-normal text-upgrade-cta"
                    >
                      <MailCheck className="size-3" aria-hidden="true" />
                      {t('leadHasVerifiedEmail')}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
              {lead.fitReason ? (
                <div className="mt-1 text-sm text-muted-foreground/80">
                  {lead.fitReason}
                </div>
              ) : null}
            </div>
            <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              {lead.locked ? (
                <>
                  <Lock className="size-3.5" aria-hidden="true" />
                  {t('leadLocked')}
                </>
              ) : (
                <>
                  <Mail
                    className="size-3.5 text-upgrade-cta"
                    aria-hidden="true"
                  />
                  {t('leadWithEmail')}
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function GtmEmailsPanel({
  emails,
  leads,
}: {
  emails: Array<GtmEmailDraft>
  leads: Array<GtmLeadPreview>
}) {
  const { t } = useTranslation('gtm')
  const leadById = new Map(leads.map((lead) => [lead.id, lead]))

  return (
    <section className={panelClassName}>
      <h2 className="text-xl font-semibold text-foreground">
        {t('emailsTitle')}
      </h2>
      <div className="mt-5 space-y-4">
        {emails.map((email) => {
          const lead = leadById.get(email.leadId)
          return (
            <article
              key={email.leadId}
              className="rounded-xl border border-sidebar-border bg-card/80 p-4"
            >
              {lead ? (
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    {t('emailTo', {
                      name: lead.fullName,
                      title: lead.title,
                      company: lead.companyName,
                    })}
                  </span>
                  {lead.campaign ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-sidebar-border px-2 py-0 text-[11px] font-normal text-muted-foreground"
                    >
                      {lead.campaign}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-2 text-sm text-foreground">
                <span className="text-muted-foreground">
                  {t('emailSubject')}:
                </span>{' '}
                {email.subject}
              </div>
              <p className="mt-3 text-sm leading-6 whitespace-pre-line text-foreground/80">
                {email.body}
              </p>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export function GtmEstimatePanel({
  estimate,
}: {
  estimate: GtmFunnelEstimate
}) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const { example } = estimate
  const number = new Intl.NumberFormat(lang)
  const range = (value: { min: number; max: number }) =>
    value.min === value.max
      ? number.format(value.min)
      : `${number.format(value.min)}–${number.format(value.max)}`

  return (
    <section className={cn(panelClassName, 'p-4 sm:p-6')}>
      <h2 className="text-xl font-semibold text-foreground">
        {t('estimateTitle', { budget: example.budgetUsd })}
      </h2>
      <dl className="mt-4 grid gap-2 sm:mt-5 sm:grid-cols-2 sm:gap-3">
        {[
          {
            label: t('estimateReplies'),
            value: range(example.positiveReplies),
          },
          { label: t('estimateMeetings'), value: range(example.meetings) },
        ].map((item) => (
          <div
            key={item.label}
            className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-sidebar-border bg-card/80 p-3 sm:block"
          >
            <dt className="min-w-0 text-xs leading-4 text-muted-foreground">
              {item.label}
            </dt>
            <dd className="shrink-0 text-xl font-semibold text-foreground tabular-nums sm:mt-1 sm:text-2xl">
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-xs text-muted-foreground">{t('estimateNote')}</p>
    </section>
  )
}

export function GtmStartPanel({
  domain,
  runId,
  onTryAnother,
  onStart,
  showStartAction = true,
}: {
  domain: string
  /** Without onStart the button opens the run, whose step 6 has checkout. */
  runId?: string
  onTryAnother: () => void
  /** Opens checkout. */
  onStart?: () => void
  showStartAction?: boolean
}) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const ctaClassName =
    'rounded-xl bg-upgrade-cta px-6 text-upgrade-cta-foreground hover:bg-upgrade-cta/90'

  return (
    <section
      className={cn(
        panelClassName,
        'flex flex-col justify-between gap-6 border-upgrade-cta/40',
      )}
    >
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          {t(showStartAction ? 'ctaTitle' : 'ctaHowItWorks')}
        </h2>
        <p className="mt-2 text-muted-foreground">{t('ctaText')}</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {!showStartAction ? null : onStart ? (
          <Button
            type="button"
            size="lg"
            className={ctaClassName}
            onClick={() => {
              trackClickGtmStartCampaign({ domain })
              onStart()
            }}
          >
            {t('ctaButton')}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button asChild size="lg" className={ctaClassName}>
            <Link
              to={runId ? '/$lang/gtm/$runId' : '/$lang'}
              params={{ lang, runId: runId ?? '' }}
              onClick={() => trackClickGtmStartCampaign({ domain })}
            >
              {t('ctaButton')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        )}
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="rounded-xl border-border bg-card/50 px-6"
          onClick={onTryAnother}
        >
          {t('tryAnother')}
        </Button>
      </div>
    </section>
  )
}
